import React from 'react';
import './Privacy.css';

const TOC_LINKS = [
  { href: '#overview',     label: '1. Overview' },
  { href: '#data-collected', label: '2. Data Collected' },
  { href: '#keystroke-data', label: '3. Keystroke Data' },
  { href: '#cookies',      label: '4. Cookies' },
  { href: '#third-party',  label: '5. Third-Party' },
  { href: '#data-retention', label: '6. Retention' },
  { href: '#your-rights',  label: '7. Your Rights' },
  { href: '#children',     label: '8. Children' },
  { href: '#changes',      label: '9. Changes' },
  { href: '#contact',      label: '10. Contact' },
];

export default function Privacy() {
  return (
    <main className="privacy-page">
      <div className="page-hero">
        <div className="container">
          <div className="hero-badge">Legal</div>
          <h1>Privacy <span className="glow-text">Policy</span></h1>
          <p>Last updated: January 1, 2025. We believe in full transparency about how we handle your data.</p>
        </div>
      </div>

      <div className="container">
        {/* Mobile TOC — horizontal scroll pills */}
        <div className="privacy-toc-mobile">
          {TOC_LINKS.map(l => (
            <a key={l.href} href={l.href}>{l.label}</a>
          ))}
        </div>

        <div className="privacy-layout">
          {/* Desktop sticky sidebar TOC */}
          <aside className="privacy-toc">
            <h4>Contents</h4>
            <ul>
              {TOC_LINKS.map(l => (
                <li key={l.href}><a href={l.href}>{l.label}</a></li>
              ))}
            </ul>
          </aside>

          <div className="privacy-content">
            <section id="overview" className="policy-section">
              <h2>1. Overview</h2>
              <p>KeyTest ("we", "us", "our") is committed to protecting your privacy. This Privacy Policy explains what information we collect when you use our keyboard testing service, how we use it, and what rights you have regarding your data.</p>
              <p>By using KeyTest, you agree to the practices described in this policy. If you do not agree, please discontinue use of our service.</p>
              <div className="highlight-box">
                <strong>The short version:</strong> We do not log, store, or transmit your keystrokes. Ever. The keyboard and mouse testing functionality works entirely within your browser and no input data leaves your device.
              </div>
            </section>

            <section id="data-collected" className="policy-section">
              <h2>2. Data We Collect</h2>
              <p>We collect minimal data necessary to operate and improve the service:</p>
              <h4>Anonymous Analytics</h4>
              <p>We use privacy-respecting analytics to understand general usage patterns. This may include pages visited, general geographic region (country-level only), browser type and version, operating system type, referring website, and screen resolution category.</p>
              <p>This data is fully anonymized and cannot be used to identify you as an individual. We do not create user profiles or track individuals across sessions.</p>
              <h4>Contact Form Data</h4>
              <p>If you submit our contact form, we collect your name, email address, selected subject, and message content. This data is used solely to respond to your inquiry and is not shared with third parties.</p>
            </section>

            <section id="keystroke-data" className="policy-section">
              <h2>3. Keystroke & Mouse Data — Our Core Commitment</h2>
              <p>KeyTest is an input testing tool, which means understanding how we handle keystroke and mouse data is critically important. Here is our unambiguous commitment:</p>
              <div className="commitment-list">
                {[
                  { title: 'All processing happens locally in your browser.', body: 'JavaScript event listeners capture keypress and mouse events and update the on-screen visualizations. This entire process happens in your browser\'s memory — no network request is made for input events.' },
                  { title: 'We cannot see what keys you press or where you click.', body: 'Our servers have no mechanism to receive keystroke or mouse click data. The code that handles input events is entirely client-side JavaScript with no server communication.' },
                  { title: 'Input data is never stored.', body: 'All key and mouse event data exists only in your browser\'s working memory during your active session. It is automatically cleared when you close or refresh the page.' },
                  { title: 'You can verify this yourself.', body: 'Open your browser\'s developer tools (F12), go to the Network tab, and press keys or click buttons. You will observe zero outbound network requests generated by input activity.' },
                ].map((item, i) => (
                  <div key={i} className="commitment-item">
                    <span className="c-icon green">✓</span>
                    <div>
                      <strong>{item.title}</strong>
                      <p>{item.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section id="cookies" className="policy-section">
              <h2>4. Cookies</h2>
              <p>KeyTest uses a minimal number of cookies necessary for the service to function:</p>
              <div className="table-scroll">
                <table className="policy-table">
                  <thead>
                    <tr><th>Cookie</th><th>Purpose</th><th>Duration</th><th>Type</th></tr>
                  </thead>
                  <tbody>
                    <tr><td>_session</td><td>Maintains session state</td><td>Session</td><td>Functional</td></tr>
                    <tr><td>_analytics_id</td><td>Anonymous usage analytics</td><td>12 months</td><td>Analytics</td></tr>
                    <tr><td>kt-theme</td><td>Stores your light/dark preference</td><td>12 months</td><td>Preference</td></tr>
                  </tbody>
                </table>
              </div>
              <p>We do not use advertising cookies, tracking pixels, or third-party marketing cookies. You can disable cookies in your browser settings, though some features may not function correctly without session cookies.</p>
            </section>

            <section id="third-party" className="policy-section">
              <h2>5. Third-Party Services</h2>
              <p>KeyTest uses a limited number of third-party services to operate:</p>
              <ul>
                <li><strong>Google Fonts:</strong> We load web fonts from Google Fonts. Google may log font requests — see Google's privacy policy for details.</li>
                <li><strong>Web Hosting Provider:</strong> Our hosting provider processes server access logs (IP addresses, timestamps, pages requested) as part of standard server operation. These logs are retained for a maximum of 30 days for security purposes.</li>
                <li><strong>Analytics Service:</strong> We use an analytics provider that processes anonymized usage data. No personally identifiable information is shared.</li>
              </ul>
              <p>We do not sell, rent, or share your personal data with any third party for commercial purposes.</p>
            </section>

            <section id="data-retention" className="policy-section">
              <h2>6. Data Retention</h2>
              <ul>
                <li><strong>Contact form submissions:</strong> Retained for 12 months after last correspondence, then permanently deleted.</li>
                <li><strong>Anonymous analytics:</strong> Retained in aggregated, anonymized form for up to 24 months.</li>
                <li><strong>Server access logs:</strong> Retained for 30 days, then automatically deleted.</li>
                <li><strong>Keystroke &amp; mouse data:</strong> Never retained — not collected at the server level.</li>
              </ul>
            </section>

            <section id="your-rights" className="policy-section">
              <h2>7. Your Rights</h2>
              <p>Depending on your jurisdiction, you may have the following rights regarding your personal data: right to access, right to erasure, right to portability, right to object, and right to rectification.</p>
              <p>To exercise any of these rights, contact us via our Contact page. We will respond within 30 days. If you are located in the EEA, you also have the right to lodge a complaint with your local data protection authority.</p>
            </section>

            <section id="children" className="policy-section">
              <h2>8. Children's Privacy</h2>
              <p>KeyTest does not knowingly collect personal data from children under the age of 13. If you believe a child under 13 has submitted personal data to us, please contact us immediately and we will delete that information promptly.</p>
            </section>

            <section id="changes" className="policy-section">
              <h2>9. Changes to This Policy</h2>
              <p>We may update this Privacy Policy from time to time. When we make significant changes, we will update the "Last updated" date at the top of this page. Continued use of KeyTest after a policy update constitutes acceptance of the revised policy.</p>
            </section>

            <section id="contact" className="policy-section">
              <h2>10. Contact Us</h2>
              <p>If you have questions about this Privacy Policy or wish to exercise your data rights, please reach out through our <a href="/contact">Contact page</a>. We respond to all requests within 30 days.</p>
            </section>
          </div>
        </div>
      </div>
    </main>
  );
}
